import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '@/api/client'
import type { FeedbackItem, FeedbackStatus } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { FeedbackForm } from '@/components/HelpPanel'
import { Markdown } from '@/components/Markdown'
import { ARTICLES, SECTIONS, articleBySlug, isFor } from '@/help'
import { useCertAccess } from '@/lib/certs'

/**
 * /help and /help/:slug -- every article, grouped and searchable, with Send
 * feedback and "Your feedback" underneath. /help/inbox is the Admins' list of
 * everything sent.
 *
 * The list shows what applies to the person (a supervisor is not shown the
 * office setup articles), but a direct link to any article still opens it:
 * help is not secret, and a link Tom sends someone should work.
 */
export function HelpCenter() {
  const { slug } = useParams()
  const { user } = useAuth()
  const certs = useCertAccess()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const isAdmin = user?.role === 'Admin'
  const viewer = { role: user?.role, certs: certs.enabled && certs.can('view_own') }

  const mine = ARTICLES.filter((a) => isFor(a, viewer))
  const article = articleBySlug(slug) ?? articleBySlug('welcome') ?? ARTICLES[0]

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase()
    if (!t) return mine
    return mine.filter((a) => `${a.title} ${a.summary} ${a.body}`.toLowerCase().includes(t))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, mine.length])

  useEffect(() => window.scrollTo(0, 0), [slug])

  return (
    <div className="help-center">
      <div className="help-center-top">
        <button type="button" className="btn-plain" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <span className="muted">Press ? on any screen for help with that screen.</span>
      </div>
      <div className="help-layout">
        <nav className="help-index card" aria-label="Help topics">
          <input
            type="search"
            placeholder="Search help"
            aria-label="Search help"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {isAdmin && (
            <Link to="/help/inbox" className={slug === 'inbox' ? 'active' : ''}>
              Feedback inbox
              <InboxCount />
            </Link>
          )}
          {SECTIONS.map((section) => {
            const items = matches.filter((a) => a.section === section)
            if (!items.length) return null
            return (
              <div key={section} className="help-index-section">
                <div className="help-kicker">{section}</div>
                {items.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/help/${a.slug}`}
                    className={slug !== 'inbox' && a.slug === article.slug ? 'active' : ''}
                    onClick={() => setQ('')}
                  >
                    {a.title}
                  </Link>
                ))}
              </div>
            )
          })}
          {matches.length === 0 && <p className="muted">Nothing matches “{q}”.</p>}
        </nav>

        <div className="help-main stack">
          {slug === 'inbox' && isAdmin ? (
            <FeedbackInbox />
          ) : (
            <>
              <article className="card">
                <div className="help-kicker">{article.section}</div>
                <h2 className="help-title">{article.title}</h2>
                <p className="muted">{article.summary}</p>
                <Markdown source={article.body} />
              </article>
              <div className="card">
                <FeedbackForm />
              </div>
              <MyFeedback />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const STATUS_LABEL: Record<FeedbackStatus, string> = { New: 'Sent', Seen: 'Seen', Done: 'Done' }
const CATEGORY_LABEL = { Bug: 'Something’s wrong', Question: 'Question', Idea: 'Idea' } as const

/** What this person has sent, with any reply. Nothing shows until they have sent something. */
function MyFeedback() {
  const [items, setItems] = useState<FeedbackItem[] | null>(null)
  useEffect(() => {
    api.myFeedback().then(setItems, () => setItems(null))
  }, [])
  if (!items || items.length === 0) return null
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Your feedback</h3>
      <ul className="feedback-list">
        {items.map((f) => (
          <li key={f.feedback_id}>
            <div className="feedback-meta">
              <span className={`feedback-status is-${f.status.toLowerCase()}`}>{STATUS_LABEL[f.status]}</span>
              <span>{CATEGORY_LABEL[f.category]}</span>
              <span className="muted">{when(f.created_utc)}</span>
            </div>
            <div className="feedback-message">{f.message}</div>
            {f.admin_note && <div className="feedback-reply">Reply: {f.admin_note}</div>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The New count beside "Feedback inbox". */
function InboxCount() {
  const [n, setN] = useState(0)
  useEffect(() => {
    api.allFeedback('New').then((r) => setN(r.length), () => setN(0))
  }, [])
  return n > 0 ? <span className="nav-badge">{n}</span> : null
}

/** Admins: everything sent, newest first. Mark Seen or Done, and reply. */
function FeedbackInbox() {
  const [filter, setFilter] = useState<FeedbackStatus | ''>('New')
  const [items, setItems] = useState<FeedbackItem[] | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    setItems(null)
    api.allFeedback(filter || undefined).then(setItems, (e) => setErr((e as Error).message))
  }, [filter])

  function replace(updated: FeedbackItem) {
    setItems((xs) =>
      (xs ?? [])
        .map((x) => (x.feedback_id === updated.feedback_id ? updated : x))
        .filter((x) => !filter || x.status === filter),
    )
  }

  return (
    <div className="card">
      <div className="help-inbox-head">
        <h2 className="help-title" style={{ margin: 0 }}>
          Feedback inbox
        </h2>
        <label>
          Show{' '}
          <select value={filter} onChange={(e) => setFilter(e.target.value as FeedbackStatus | '')}>
            <option value="New">New</option>
            <option value="Seen">Seen</option>
            <option value="Done">Done</option>
            <option value="">All</option>
          </select>
        </label>
      </div>
      <p className="muted">
        What people sent from Send feedback, with the screen they were on. A reply shows to the sender under “Your
        feedback” in Help. Nothing is emailed.
      </p>
      {err && <div className="alert alert-error">{err}</div>}
      {items === null && !err && <p className="muted">Loading…</p>}
      {items?.length === 0 && <p className="muted">Nothing here.</p>}
      <ul className="feedback-list">
        {items?.map((f) => <InboxRow key={f.feedback_id} f={f} onSaved={replace} />)}
      </ul>
    </div>
  )
}

function InboxRow({ f, onSaved }: { f: FeedbackItem; onSaved: (f: FeedbackItem) => void }) {
  const [note, setNote] = useState(f.admin_note ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save(status?: FeedbackStatus) {
    setBusy(true)
    setErr(null)
    try {
      const payload: { status?: FeedbackStatus; admin_note?: string | null } = {}
      if (status) payload.status = status
      if (note !== (f.admin_note ?? '')) payload.admin_note = note
      if (!payload.status && payload.admin_note === undefined) return
      onSaved(await api.updateFeedback(f.feedback_id, payload))
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li>
      <div className="feedback-meta">
        <span className={`feedback-status is-${f.status.toLowerCase()}`}>{f.status}</span>
        <strong>{f.sender_name ?? `#${f.employee_id}`}</strong>
        <span>{CATEGORY_LABEL[f.category]}</span>
        <span className="muted">{when(f.created_utc)}</span>
        {f.page && (
          <Link to={f.page} className="feedback-page">
            {f.page}
          </Link>
        )}
      </div>
      <div className="feedback-message">{f.message}</div>
      <textarea
        rows={2}
        placeholder="Reply (optional) — the sender sees this in Help"
        value={note}
        maxLength={2000}
        onChange={(e) => setNote(e.target.value)}
      />
      {err && <div className="alert alert-error">{err}</div>}
      <div className="help-feedback-actions">
        {f.status === 'New' && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void save('Seen')}>
            Mark seen
          </button>
        )}
        {f.status !== 'Done' && (
          <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void save('Done')}>
            Done
          </button>
        )}
        {note !== (f.admin_note ?? '') && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void save()}>
            Save reply
          </button>
        )}
      </div>
    </li>
  )
}
